import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock, Download, Loader2, Play } from "lucide-react";
import { api } from "@/lib/api";
import { saveBlob } from "@/components/payroll/bankUtils";

const STATUS = { hazir: "Hazır", hazirlaniyor: "Hazırlanıyor", hata: "Hata" };
const mb = (n) => `${((n || 0) / 1048576).toFixed(2)} MB`;

const EmailSetting = ({ initial }) => {
  const [email, setEmail] = useState(initial || "");
  useEffect(() => setEmail(initial || ""), [initial]);
  const save = () => api.put("/admin/backup/settings", { email: email.trim() || null })
    .then(() => toast.success("Adres kaydedildi")).catch(() => toast.error("Geçerli bir e-posta girin"));
  return (
    <div className="flex items-center gap-2 flex-wrap mb-3">
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="yedek@akathukuk.com"
        className="border border-slate-300 rounded-md px-2 py-1.5 text-sm w-64" data-testid="auto-backup-email" />
      <button type="button" onClick={save} className="px-3 py-1.5 text-sm rounded-md border border-slate-300 hover:bg-white" data-testid="auto-backup-email-save">Kaydet</button>
      <span className="text-[11px] text-amber-700">E-posta gönderimi henüz açık değil; adres ileride kullanılmak üzere saklanır.</span>
    </div>
  );
};

const Row = ({ b }) => {
  const [busy, setBusy] = useState(false);
  const dl = async () => {
    setBusy(true);
    try { saveBlob(await api.get(`/admin/backup/auto/${b.id}/download`, { responseType: "blob", timeout: 0 }), "akat_yedek.zip"); }
    catch { toast.error("Yedek indirilemedi"); } finally { setBusy(false); }
  };
  return (
    <li className="flex items-center gap-3 text-xs py-1.5 border-b border-slate-200 last:border-0" data-testid={`auto-backup-row-${b.id}`}>
      <span className="w-36">{new Date(b.at).toLocaleString("tr-TR")}</span>
      <span className="text-slate-500 flex-1 truncate">{b.trigger}</span>
      <span className={b.durum === "hata" ? "text-red-600" : "text-slate-600"} title={b.hata || ""}>{STATUS[b.durum]}{b.size ? ` · ${mb(b.size)}` : ""}</span>
      {b.durum === "hazir" && (
        <button type="button" onClick={dl} disabled={busy} className="text-slate-700 hover:text-slate-900" data-testid={`auto-backup-download-${b.id}`}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
        </button>
      )}
    </li>
  );
};

export default function AutoBackupPanel() {
  const [data, setData] = useState({ items: [], email: null });
  const [running, setRunning] = useState(false);
  const load = () => api.get("/admin/backup/auto").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const runNow = async () => {
    setRunning(true);
    try { await api.post("/admin/backup/auto/run", {}, { timeout: 0 }); toast.success("Yedek alındı ve saklandı"); load(); }
    catch { toast.error("Yedek alınamadı"); } finally { setRunning(false); }
  };
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mt-3" data-testid="auto-backup-panel">
      <div className="flex items-center gap-2 mb-1"><CalendarClock size={16} className="text-slate-700" /><h4 className="font-semibold text-sm">Otomatik Haftalık Yedek</h4></div>
      <p className="text-xs text-slate-600 mb-3">Her pazar 23:00'te veriler + Excel yedeği (dosyalar hariç) otomatik alınır ve burada saklanır. Son 12 yedek listelenir.</p>
      <EmailSetting initial={data.email} />
      <button type="button" onClick={runNow} disabled={running} data-testid="auto-backup-run-now"
        className="inline-flex items-center gap-2 px-3 py-1.5 mb-3 border border-slate-300 rounded-md text-sm hover:bg-white disabled:opacity-50">
        {running ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />} Şimdi otomatik yedek al
      </button>
      {data.items.length ? <ul data-testid="auto-backup-list">{data.items.map((b) => <Row key={b.id} b={b} />)}</ul>
        : <p className="text-xs text-slate-500" data-testid="auto-backup-empty">Henüz otomatik yedek yok.</p>}
    </div>
  );
}
